import { useTranslate } from '@refinedev/core';
import { Flex, Progress, Typography, theme } from 'antd';
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import type { ModStatus } from '@/api/generated/schemas';
import type { RunStep } from './enablePlan';
import type { ItemErrorGroup } from '@/components/user_catalog/modStatusBulk';
import { ModStatusRefusalGroups } from '@/components/user_catalog/ModStatusRefusalGroups';

const { Text } = Typography;

/**
 * `skipped` = nothing to send, every object was already at the target;
 * `aborted` = the user stopped the run before this step was sent;
 * `partial` = sent, but the backend refused some objects (the rest were
 * saved) — a finished step: retrying would be refused again.
 */
export type StepState =
  | 'queued'
  | 'running'
  | 'done'
  | 'partial'
  | 'failed'
  | 'skipped'
  | 'aborted';

/** Sent and answered: `done` or `partial`. */
const isAnswered = (state: StepState) =>
  state === 'done' || state === 'partial';

export interface StepProgress extends RunStep {
  state: StepState;
  error?: string;
  /** Refusals by reason — the failed request's, or a partial step's. */
  itemErrors?: ItemErrorGroup[];
  /** Objects a `partial` step did change, out of `count`. */
  changed?: number;
}

interface RunProgressProps {
  steps: StepProgress[];
  status: ModStatus;
}

/** One request per entity kind, shown as a checklist while the run goes. */
export const RunProgress = ({ steps, status }: RunProgressProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.publications.enable_modal.run.${key}`, opts);

  const done = steps.filter(
    (s) => isAnswered(s.state) || s.state === 'skipped'
  ).length;
  const failed = steps.some((s) => s.state === 'failed');
  const partial = steps.some((s) => s.state === 'partial');
  const aborted = steps.some((s) => s.state === 'aborted');
  const running = steps.some((s) => s.state === 'running');

  const tone = (s: StepProgress) =>
    ({
      done: {
        bg: token.colorSuccessBg,
        border: token.colorSuccessBorder,
        icon: token.colorSuccess,
      },
      partial: {
        bg: token.colorWarningBg,
        border: token.colorWarningBorder,
        icon: token.colorWarning,
      },
      failed: {
        bg: token.colorErrorBg,
        border: token.colorErrorBorder,
        icon: token.colorError,
      },
      running: {
        bg: token.colorPrimaryBg,
        border: token.colorPrimaryBorder,
        icon: token.colorPrimary,
      },
      queued: {
        bg: token.colorBgContainer,
        border: token.colorBorderSecondary,
        icon: token.colorTextQuaternary,
      },
      skipped: {
        bg: token.colorFillQuaternary,
        border: token.colorBorderSecondary,
        icon: token.colorTextTertiary,
      },
      aborted: {
        bg: token.colorFillQuaternary,
        border: token.colorBorderSecondary,
        icon: token.colorTextTertiary,
      },
    })[s.state];

  const icon = (s: StepProgress) => {
    const size = 28;
    const c = tone(s);
    const base = {
      width: size,
      height: size,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    } as const;
    if (s.state === 'done')
      return (
        <span style={{ ...base, background: c.icon, color: token.colorWhite }}>
          <CheckOutlined />
        </span>
      );
    if (s.state === 'failed')
      return (
        <span style={{ ...base, background: c.icon, color: token.colorWhite }}>
          <CloseOutlined />
        </span>
      );
    if (s.state === 'partial')
      return (
        <span style={{ ...base, background: c.icon, color: token.colorWhite }}>
          <ExclamationOutlined />
        </span>
      );
    if (s.state === 'skipped' || s.state === 'aborted')
      return (
        <span
          style={{
            ...base,
            border: `${token.lineWidth}px dashed ${c.icon}`,
            color: c.icon,
          }}
        >
          <MinusOutlined />
        </span>
      );
    return (
      <span
        style={{
          ...base,
          border: `${token.lineWidth}px solid ${c.icon}`,
          color: c.icon,
        }}
      >
        {s.state === 'running' && <ArrowRightOutlined />}
      </span>
    );
  };

  const kindName = (name: string) =>
    t(`user_catalog.publications.enable_modal.kinds.${name}`);

  /**
   * Formats ride on the publications request, so that one step names both —
   * each with its own count, matching what was confirmed on the setup screen.
   */
  const label = (s: StepProgress) => {
    if (s.kind === 'formats') return `${kindName('formats')} (${s.formats})`;
    if (s.kind !== 'publications') return `${kindName(s.kind)} (${s.count})`;
    const publications = `${kindName('publications')} (${s.count - s.formats})`;
    return s.formats > 0
      ? `${publications} · ${kindName('formats')} (${s.formats})`
      : publications;
  };

  const subtitle = (s: StepProgress) => {
    if (s.state === 'done') return k(`done.${status}`, { count: s.count });
    if (s.state === 'partial')
      return k('partial', { changed: s.changed ?? 0, count: s.count });
    if (s.state === 'failed') return s.error ?? k('failed');
    if (s.state === 'skipped') return k('skipped');
    if (s.state === 'aborted') return k('aborted');
    if (s.state === 'running') return k('running');
    return k('queued');
  };

  return (
    <Flex vertical gap={token.marginSM}>
      {/* Steps change state without moving focus — announce the run's progress. */}
      <div aria-live="polite" aria-atomic="true">
        <Flex justify="space-between" align="baseline">
          <Text strong>
            {failed
              ? k('header_failed')
              : aborted
                ? k('header_aborted')
                : running
                  ? k(`header_running.${status}`)
                  : partial
                    ? k('header_partial')
                    : k('header_done')}
          </Text>
          <Text type="secondary">
            {k('requests', { done, total: steps.length })}
          </Text>
        </Flex>
      </div>
      <Progress
        percent={steps.length ? Math.round((done / steps.length) * 100) : 0}
        showInfo={false}
        status={failed ? 'exception' : undefined}
        strokeColor={partial ? token.colorWarning : token.colorSuccess}
        aria-label={k('requests', { done, total: steps.length })}
      />
      {steps.map((s) => (
        <Flex
          key={s.kind}
          gap={token.marginSM}
          align={s.itemErrors ? 'flex-start' : 'center'}
          style={{
            border: `${token.lineWidth}px solid ${tone(s).border}`,
            background: tone(s).bg,
            borderRadius: token.borderRadiusLG,
            padding: token.padding,
            opacity: ['queued', 'skipped', 'aborted'].includes(s.state)
              ? 0.7
              : 1,
          }}
        >
          {icon(s)}
          <div style={{ minWidth: 0 }}>
            <Text strong>{label(s)}</Text>
            {/* A failed request names only its refusals; a partial step
                also says how much of it went through. */}
            {(!s.itemErrors || s.state === 'partial') && (
              <Text
                type={
                  s.state === 'failed'
                    ? 'danger'
                    : s.state === 'partial'
                      ? 'warning'
                      : 'secondary'
                }
                style={{
                  display: 'block',
                  color: s.state === 'done' ? token.colorSuccess : undefined,
                }}
              >
                {subtitle(s)}
              </Text>
            )}
            {s.itemErrors && <ModStatusRefusalGroups groups={s.itemErrors} />}
          </div>
        </Flex>
      ))}
      <Text type="secondary">{k('footnote')}</Text>
    </Flex>
  );
};
